import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Search, Filter, LayoutGrid, Columns3, GanttChart, Table2,
  ChevronDown, ChevronRight, ArrowUpDown, Users, Clock,
  CheckCircle2, AlertTriangle, TrendingUp, FolderOpen,
  GripVertical, Calendar, X, ChevronLeft, Eye,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ────────── helpers ────────── */
const pctColor = (pct) => {
  if (pct >= 90) return "text-emerald-600";
  if (pct >= 60) return "text-blue-600";
  if (pct >= 30) return "text-amber-600";
  return "text-red-500";
};
const pctBg = (pct) => {
  if (pct >= 90) return "bg-emerald-500";
  if (pct >= 60) return "bg-blue-500";
  if (pct >= 30) return "bg-amber-500";
  return "bg-red-500";
};
const statusColor = {
  Completed: "bg-emerald-100 text-emerald-700 border-emerald-200",
  "In Progress": "bg-blue-100 text-blue-700 border-blue-200",
  Pending: "bg-slate-100 text-slate-600 border-slate-200",
};
const statusDot = {
  Completed: "bg-emerald-500",
  "In Progress": "bg-blue-500",
  Pending: "bg-slate-400",
};

/* ────────── KPI Card ────────── */
const KpiCard = ({ label, value, sub, icon: Icon, color }) => (
  <div className="bg-white rounded-xl border border-slate-200/80 p-5 flex items-start gap-4 shadow-sm hover:shadow-md transition-shadow" data-testid={`kpi-${label.toLowerCase().replace(/\s/g,'-')}`}>
    <div className={`w-11 h-11 rounded-xl ${color} flex items-center justify-center shrink-0`}>
      <Icon className="w-5 h-5 text-white" />
    </div>
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}</p>
      <p className="text-2xl font-bold text-slate-900 mt-0.5 tracking-tight">{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
    </div>
  </div>
);

/* ────────── Circular Progress ────────── */
const CircularProgress = ({ pct, size = 52 }) => {
  const r = (size - 6) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (pct / 100) * circ;
  return (
    <svg width={size} height={size} className="shrink-0">
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#e2e8f0" strokeWidth="5" />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={pct >= 90 ? "#10b981" : pct >= 60 ? "#3b82f6" : pct >= 30 ? "#f59e0b" : "#ef4444"} strokeWidth="5" strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round" transform={`rotate(-90 ${size/2} ${size/2})`} className="transition-all duration-700" />
      <text x={size/2} y={size/2} textAnchor="middle" dominantBaseline="central" className="text-[11px] font-bold fill-slate-700">{Math.round(pct)}%</text>
    </svg>
  );
};

/* ══════════════════════════════════════════════
   VIEW 1: OVERVIEW CARDS
   ══════════════════════════════════════════════ */
const OverviewView = ({ projects, onSelect }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="overview-grid">
    {projects.map((p) => (
      <div
        key={p.title}
        onClick={() => onSelect(p)}
        className="group bg-white rounded-xl border border-slate-200/80 p-5 cursor-pointer hover:shadow-lg hover:border-blue-300/60 transition-all duration-200"
        data-testid={`project-card-${p.title.replace(/\s/g,'-')}`}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-slate-900 text-sm truncate group-hover:text-blue-700 transition-colors">{p.title}</h3>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {p.seasons?.length > 0 && p.seasons.map(s => (
                <Badge key={s} variant="outline" className="text-[10px] px-1.5 py-0 bg-violet-50 text-violet-600 border-violet-200">S{s.replace('.0','')}</Badge>
              ))}
              {p.task_types?.map(t => (
                <Badge key={t} variant="outline" className="text-[10px] px-1.5 py-0">{t}</Badge>
              ))}
            </div>
          </div>
          <CircularProgress pct={p.completion_pct} />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-3">
          <div className="text-center p-2 bg-slate-50 rounded-lg">
            <p className="text-lg font-bold text-slate-900">{p.total_tasks}</p>
            <p className="text-[10px] text-slate-500 uppercase tracking-wide">Tasks</p>
          </div>
          <div className="text-center p-2 bg-slate-50 rounded-lg">
            <p className="text-lg font-bold text-slate-900">{p.episode_count}</p>
            <p className="text-[10px] text-slate-500 uppercase tracking-wide">Episodes</p>
          </div>
          <div className="text-center p-2 bg-slate-50 rounded-lg">
            <p className="text-lg font-bold text-slate-900">{p.resource_count}</p>
            <p className="text-[10px] text-slate-500 uppercase tracking-wide">People</p>
          </div>
        </div>

        {/* Progress bar */}
        <div className="space-y-1.5">
          <div className="flex justify-between items-center text-[11px]">
            <span className="text-slate-500">Progress</span>
            <span className={`font-semibold ${pctColor(p.completion_pct)}`}>{p.completed}/{p.total_tasks}</span>
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all duration-700 ${pctBg(p.completion_pct)}`} style={{ width: `${p.completion_pct}%` }} />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
          <div className="flex items-center gap-1 text-[11px] text-slate-500">
            <Calendar className="w-3 h-3" />
            {p.latest_date || "No date"}
          </div>
          {p.overdue_count > 0 && (
            <Badge variant="outline" className="text-[10px] bg-red-50 text-red-600 border-red-200">
              {p.overdue_count} overdue
            </Badge>
          )}
          {p.top_resources?.length > 0 && (
            <div className="flex -space-x-1.5">
              {p.top_resources.slice(0, 3).map((r, i) => (
                <TooltipProvider key={i}>
                  <Tooltip>
                    <TooltipTrigger>
                      <div className="w-6 h-6 rounded-full bg-slate-200 border-2 border-white flex items-center justify-center text-[9px] font-bold text-slate-600 uppercase">
                        {r.name?.charAt(0) || "?"}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent><p className="text-xs">{r.name} ({r.count})</p></TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ))}
            </div>
          )}
        </div>
      </div>
    ))}
  </div>
);

/* ══════════════════════════════════════════════
   VIEW 2: KANBAN BOARD
   ══════════════════════════════════════════════ */
const KanbanView = ({ kanbanData, onStatusChange }) => {
  const [dragging, setDragging] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  const colMeta = {
    Pending:       { color: "border-t-slate-400",    bg: "bg-slate-50",   icon: Clock },
    "In Progress": { color: "border-t-blue-500",     bg: "bg-blue-50/50", icon: TrendingUp },
    Completed:     { color: "border-t-emerald-500",  bg: "bg-emerald-50/50", icon: CheckCircle2 },
  };

  const handleDragStart = (e, taskId, fromCol) => {
    setDragging({ taskId, fromCol });
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDrop = (e, toCol) => {
    e.preventDefault();
    setDragOver(null);
    if (dragging && dragging.fromCol !== toCol) {
      onStatusChange(dragging.taskId, toCol);
    }
    setDragging(null);
  };

  if (!kanbanData?.columns) return null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5" data-testid="kanban-board">
      {["Pending", "In Progress", "Completed"].map((col) => {
        const meta = colMeta[col];
        const Icon = meta.icon;
        const cards = kanbanData.columns[col] || [];
        return (
          <div
            key={col}
            className={`rounded-xl border-t-4 ${meta.color} ${meta.bg} border border-slate-200/60 min-h-[400px] flex flex-col ${dragOver === col ? "ring-2 ring-blue-400 ring-offset-2" : ""}`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(col); }}
            onDragLeave={() => setDragOver(null)}
            onDrop={(e) => handleDrop(e, col)}
            data-testid={`kanban-col-${col.replace(/\s/g,'-').toLowerCase()}`}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200/60">
              <div className="flex items-center gap-2">
                <Icon className="w-4 h-4 text-slate-500" />
                <h3 className="font-semibold text-sm text-slate-700">{col}</h3>
              </div>
              <Badge variant="secondary" className="text-[11px]">{cards.length}</Badge>
            </div>
            <div className="flex-1 p-3 space-y-2.5 overflow-y-auto max-h-[600px]">
              {cards.slice(0, 100).map((card) => (
                <div
                  key={card.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, card.id, col)}
                  className="bg-white rounded-lg border border-slate-200/80 p-3.5 cursor-grab active:cursor-grabbing hover:shadow-md transition-all group"
                  data-testid={`kanban-card-${card.id}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <p className="text-[13px] font-medium text-slate-800 truncate">{card.title}</p>
                      <p className="text-[11px] text-slate-500 truncate">{card.episode}</p>
                    </div>
                    <GripVertical className="w-4 h-4 text-slate-300 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0">{card.task_type}</Badge>
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0">{card.language}</Badge>
                  </div>
                  <div className="flex items-center justify-between mt-2.5 pt-2 border-t border-slate-100">
                    <div className="flex items-center gap-1.5">
                      <div className="w-5 h-5 rounded-full bg-slate-200 flex items-center justify-center text-[9px] font-bold text-slate-600 uppercase">
                        {card.resource_name?.charAt(0) || "?"}
                      </div>
                      <span className="text-[11px] text-slate-600 truncate max-w-[80px]">{card.resource_name}</span>
                    </div>
                    {card.due_date && (
                      <span className="text-[10px] text-slate-400 flex items-center gap-0.5">
                        <Calendar className="w-3 h-3" />{card.due_date}
                      </span>
                    )}
                  </div>
                </div>
              ))}
              {cards.length > 100 && (
                <p className="text-center text-xs text-slate-400 py-2">+{cards.length - 100} more tasks</p>
              )}
              {cards.length === 0 && (
                <div className="flex flex-col items-center justify-center py-10 text-slate-400">
                  <Icon className="w-8 h-8 mb-2 opacity-40" />
                  <p className="text-xs">No tasks</p>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};


/* ══════════════════════════════════════════════
   VIEW 3: TIMELINE / GANTT
   ══════════════════════════════════════════════ */
const TimelineView = ({ timeline }) => {
  const [expanded, setExpanded] = useState({});
  const [searchTl, setSearchTl] = useState("");

  if (!timeline?.length) return <p className="text-center text-slate-400 py-10">No timeline data</p>;

  // Filter
  const items = timeline.filter(t => t.start && t.end && (!searchTl || t.title.toLowerCase().includes(searchTl.toLowerCase())));

  // Compute date range from visible items
  const allDates = items.flatMap(t => [t.start, t.end]).filter(Boolean).sort();
  const minDate = allDates[0] || "2022-01-01";
  const maxDate = allDates[allDates.length - 1] || "2026-12-31";
  const minMs = new Date(minDate).getTime();
  const maxMs = new Date(maxDate).getTime();
  const rangeMs = maxMs - minMs || 1;

  const toLeft = (d) => ((new Date(d).getTime() - minMs) / rangeMs) * 100;
  const toWidth = (s, e) => Math.max(((new Date(e).getTime() - new Date(s).getTime()) / rangeMs) * 100, 0.5);

  // Generate month markers
  const monthMarkers = (() => {
    const months = [];
    let d = new Date(minDate);
    d.setDate(1);
    const end = new Date(maxDate);
    while (d <= end) {
      const iso = d.toISOString().slice(0, 10);
      const left = toLeft(iso);
      months.push({ key: d.toISOString().slice(0, 7), left, label: d.toLocaleDateString("en-US", { month: "short", year: "2-digit" }) });
      d.setMonth(d.getMonth() + 1);
    }
    // Show ~12-16 evenly spaced labels
    const step = Math.max(1, Math.floor(months.length / 14));
    return months.filter((_, i) => i % step === 0);
  })();

  return (
    <div className="bg-white rounded-xl border border-slate-200/80 overflow-hidden" data-testid="timeline-view">
      {/* Search */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200">
        <div className="relative w-60">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <Input className="h-8 pl-8 text-[12px]" placeholder="Filter timeline..." value={searchTl} onChange={e => setSearchTl(e.target.value)} />
        </div>
        <span className="text-[11px] text-slate-500">{items.length} projects</span>
      </div>

      {/* Header months */}
      <div className="flex items-center border-b border-slate-200 bg-slate-50 px-4 py-2">
        <div className="w-[280px] shrink-0 text-[11px] font-semibold text-slate-500 uppercase">Project</div>
        <div className="flex-1 relative h-6">
          {monthMarkers.map(m => (
            <span key={m.key} className="absolute text-[9px] text-slate-400 font-medium whitespace-nowrap" style={{ left: `${m.left}%` }}>{m.label}</span>
          ))}
        </div>
      </div>

      {/* Rows */}
      <div className="max-h-[600px] overflow-y-auto">
        {items.map((p) => {
          const isExpanded = expanded[p.title];
          return (
            <div key={p.title}>
              <div
                className="flex items-center border-b border-slate-100 hover:bg-slate-50/80 transition-colors cursor-pointer group"
                onClick={() => setExpanded(prev => ({ ...prev, [p.title]: !prev[p.title] }))}
              >
                <div className="w-[280px] shrink-0 px-4 py-2.5 flex items-center gap-2">
                  {p.episodes?.length > 0 ? (
                    isExpanded ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                  ) : <div className="w-3.5" />}
                  <p className="text-[12px] font-medium text-slate-700 truncate flex-1">{p.title}</p>
                  <Badge variant="outline" className={`text-[9px] px-1.5 py-0 shrink-0 ${statusColor[p.completion_pct >= 100 ? "Completed" : p.completion_pct > 0 ? "In Progress" : "Pending"]}`}>
                    {Math.round(p.completion_pct)}%
                  </Badge>
                </div>
                <div className="flex-1 relative h-8 px-2">
                  {/* Background track */}
                  <div className="absolute top-2 left-0 right-0 h-4 bg-slate-50 rounded" />
                  {/* Project bar */}
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div
                          className={`absolute top-2 h-4 rounded ${pctBg(p.completion_pct)} opacity-85 hover:opacity-100 transition-opacity`}
                          style={{ left: `${toLeft(p.start)}%`, width: `${toWidth(p.start, p.end)}%`, minWidth: "6px" }}
                        >
                          {/* Completed portion */}
                          <div className="h-full bg-white/25 rounded-l" style={{ width: `${p.completion_pct}%` }} />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs font-medium">{p.title}</p>
                        <p className="text-[10px] text-slate-400">{p.start} → {p.end} · {p.total_tasks} tasks · {Math.round(p.completion_pct)}%</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
              {/* Episode sub-rows */}
              {isExpanded && p.episodes?.map((ep) => (
                <div key={ep.name} className="flex items-center border-b border-slate-50 bg-slate-50/40">
                  <div className="w-[280px] shrink-0 pl-10 pr-4 py-1.5 flex items-center gap-2">
                    <p className="text-[11px] text-slate-500 truncate flex-1">{ep.name}</p>
                    <span className="text-[9px] text-slate-400">{ep.completed}/{ep.total}</span>
                  </div>
                  <div className="flex-1 relative h-5 px-2">
                    {ep.start && ep.end && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div
                              className={`absolute top-0.5 h-4 rounded ${pctBg(ep.pct)} opacity-60 hover:opacity-90 transition-opacity`}
                              style={{ left: `${toLeft(ep.start)}%`, width: `${toWidth(ep.start, ep.end)}%`, minWidth: "4px" }}
                            />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="text-[10px]">{ep.name}: {ep.start} → {ep.end} · {ep.completed}/{ep.total}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};


/* ══════════════════════════════════════════════
   VIEW 4: TABLE VIEW
   ══════════════════════════════════════════════ */
const TableView = ({ projects, onSelect }) => {
  const [sortKey, setSortKey] = useState("title");
  const [sortDir, setSortDir] = useState("asc");

  const sorted = useMemo(() => {
    const arr = [...projects];
    arr.sort((a, b) => {
      let va = a[sortKey], vb = b[sortKey];
      if (typeof va === "string") va = va.toLowerCase();
      if (typeof vb === "string") vb = vb.toLowerCase();
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [projects, sortKey, sortDir]);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  const SortHeader = ({ label, field }) => (
    <th
      className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500 cursor-pointer hover:text-slate-700 transition-colors whitespace-nowrap"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {label}
        <ArrowUpDown className="w-3 h-3" />
      </div>
    </th>
  );

  return (
    <div className="bg-white rounded-xl border border-slate-200/80 overflow-hidden" data-testid="table-view">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <SortHeader label="Project" field="title" />
              <SortHeader label="Tasks" field="total_tasks" />
              <SortHeader label="Episodes" field="episode_count" />
              <SortHeader label="Resources" field="resource_count" />
              <SortHeader label="Progress" field="completion_pct" />
              <SortHeader label="Overdue" field="overdue_count" />
              <SortHeader label="Amount" field="total_amount" />
              <SortHeader label="Due Date" field="latest_date" />
              <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((p) => (
              <tr key={p.title} className="border-b border-slate-100 hover:bg-slate-50/60 transition-colors">
                <td className="px-4 py-3">
                  <p className="text-[13px] font-medium text-slate-800 max-w-[200px] truncate">{p.title}</p>
                  <div className="flex gap-1 mt-0.5 flex-wrap">
                    {p.task_types?.slice(0, 3).map(t => (
                      <span key={t} className="text-[9px] px-1 py-0 bg-slate-100 rounded text-slate-500">{t}</span>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3 text-[13px] text-slate-700 font-medium">{p.total_tasks}</td>
                <td className="px-4 py-3 text-[13px] text-slate-600">{p.episode_count}</td>
                <td className="px-4 py-3 text-[13px] text-slate-600">{p.resource_count}</td>
                <td className="px-4 py-3 w-[160px]">
                  <div className="flex items-center gap-2.5">
                    <div className="flex-1">
                      <Progress value={p.completion_pct} className="h-2" />
                    </div>
                    <span className={`text-[12px] font-semibold ${pctColor(p.completion_pct)}`}>{Math.round(p.completion_pct)}%</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  {p.overdue_count > 0 ? (
                    <Badge variant="outline" className="text-[10px] bg-red-50 text-red-600 border-red-200">{p.overdue_count}</Badge>
                  ) : (
                    <span className="text-[11px] text-slate-400">-</span>
                  )}
                </td>
                <td className="px-4 py-3 text-[13px] text-slate-700 font-medium">
                  {p.total_amount > 0 ? `₹${p.total_amount.toLocaleString("en-IN")}` : "-"}
                </td>
                <td className="px-4 py-3 text-[12px] text-slate-500">{p.latest_date || "-"}</td>
                <td className="px-4 py-3">
                  <Button variant="ghost" size="sm" className="h-7 text-[11px]" onClick={() => onSelect(p)} data-testid={`view-project-${p.title.replace(/\s/g,'-')}`}>
                    <Eye className="w-3.5 h-3.5 mr-1" />View
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};


/* ══════════════════════════════════════════════
   PROJECT DETAIL PANEL
   ══════════════════════════════════════════════ */
const ProjectDetailPanel = ({ project, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedEps, setExpandedEps] = useState({});

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/project-tracker/projects/${encodeURIComponent(project.title)}/details`)
      .then(res => setDetail(res.data))
      .catch(() => toast.error("Failed to load project details"))
      .finally(() => setLoading(false));
  }, [project.title]);

  if (loading) return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center" onClick={onClose}>
      <div className="bg-white rounded-2xl p-8 animate-pulse">Loading...</div>
    </div>
  );
  if (!detail) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose} data-testid="project-detail-panel">
      <div className="bg-white w-full max-w-3xl h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={onClose} data-testid="close-detail-btn">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div>
              <h2 className="text-lg font-bold text-slate-900">{detail.title}</h2>
              <p className="text-xs text-slate-500">{detail.total_episodes} episodes · {detail.total_tasks} tasks · {detail.resource_count} resources</p>
            </div>
          </div>
          <CircularProgress pct={detail.completion_pct} size={48} />
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-4 gap-3 px-6 py-4">
          <div className="text-center p-3 bg-emerald-50 rounded-xl">
            <p className="text-xl font-bold text-emerald-700">{detail.episodes?.reduce((s,e)=>s+e.completed,0)}</p>
            <p className="text-[10px] text-emerald-600 uppercase tracking-wide mt-0.5">Completed</p>
          </div>
          <div className="text-center p-3 bg-blue-50 rounded-xl">
            <p className="text-xl font-bold text-blue-700">{detail.episodes?.reduce((s,e)=>s+e.in_progress,0)}</p>
            <p className="text-[10px] text-blue-600 uppercase tracking-wide mt-0.5">In Progress</p>
          </div>
          <div className="text-center p-3 bg-slate-50 rounded-xl">
            <p className="text-xl font-bold text-slate-700">{detail.episodes?.reduce((s,e)=>s+e.pending,0)}</p>
            <p className="text-[10px] text-slate-600 uppercase tracking-wide mt-0.5">Pending</p>
          </div>
          <div className="text-center p-3 bg-amber-50 rounded-xl">
            <p className="text-xl font-bold text-amber-700">{detail.episodes?.reduce((s,e)=>s+e.overdue_count,0)}</p>
            <p className="text-[10px] text-amber-600 uppercase tracking-wide mt-0.5">Overdue</p>
          </div>
        </div>

        {/* Episodes */}
        <div className="px-6 pb-6 space-y-2">
          <h3 className="text-sm font-semibold text-slate-700 mb-2">Episodes</h3>
          {detail.episodes?.map((ep) => {
            const isOpen = expandedEps[ep.episode];
            return (
              <div key={ep.episode} className="border border-slate-200 rounded-lg overflow-hidden">
                <div
                  className="flex items-center justify-between px-4 py-3 bg-slate-50/50 cursor-pointer hover:bg-slate-100/60 transition-colors"
                  onClick={() => setExpandedEps(prev => ({ ...prev, [ep.episode]: !prev[ep.episode] }))}
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    {isOpen ? <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
                    <p className="text-[13px] font-medium text-slate-700 truncate">{ep.episode}</p>
                    {ep.season && <Badge variant="outline" className="text-[9px] px-1.5 py-0 bg-violet-50 text-violet-600 border-violet-200">S{String(ep.season).replace('.0','')}</Badge>}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[11px] text-emerald-600 font-medium">{ep.completed}</span>
                      <span className="text-[11px] text-slate-300">/</span>
                      <span className="text-[11px] text-slate-500">{ep.total_tasks}</span>
                    </div>
                    <div className="w-16">
                      <Progress value={ep.completion_pct} className="h-1.5" />
                    </div>
                    {ep.overdue_count > 0 && <Badge variant="outline" className="text-[9px] bg-red-50 text-red-600 border-red-200">{ep.overdue_count}</Badge>}
                  </div>
                </div>
                {isOpen && (
                  <div className="border-t border-slate-200">
                    <table className="w-full">
                      <thead className="bg-slate-50/80">
                        <tr>
                          <th className="px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase text-left">Type</th>
                          <th className="px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase text-left">Resource</th>
                          <th className="px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase text-left">Language</th>
                          <th className="px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase text-left">Due</th>
                          <th className="px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase text-left">Status</th>
                          <th className="px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ep.tasks?.map((t) => (
                          <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50/40">
                            <td className="px-3 py-2 text-[12px] text-slate-700">{t.task_type}</td>
                            <td className="px-3 py-2 text-[12px] text-slate-600">{t.resource_name}</td>
                            <td className="px-3 py-2 text-[12px] text-slate-500">{t.language}</td>
                            <td className="px-3 py-2 text-[12px] text-slate-500">{t.due_date || "-"}</td>
                            <td className="px-3 py-2">
                              <div className="flex items-center gap-1.5">
                                <div className={`w-1.5 h-1.5 rounded-full ${statusDot[t.status] || "bg-slate-400"}`} />
                                <span className="text-[11px] text-slate-600">{t.status}</span>
                              </div>
                            </td>
                            <td className="px-3 py-2 text-[12px] text-slate-700 text-right font-medium">
                              {t.amount > 0 ? `₹${t.amount.toLocaleString("en-IN")}` : "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};


/* ══════════════════════════════════════════════
   WORKLOAD VIEW (embedded in overview)
   ══════════════════════════════════════════════ */
const WorkloadBar = ({ workload }) => {
  if (!workload?.resources?.length) return null;
  const top = workload.resources.slice(0, 15);
  const maxTasks = Math.max(...top.map(r => r.total_tasks), 1);

  return (
    <div className="bg-white rounded-xl border border-slate-200/80 p-5 mt-5" data-testid="workload-section">
      <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
        <Users className="w-4 h-4 text-slate-500" />
        Resource Workload (Top 15)
      </h3>
      <div className="space-y-2.5">
        {top.map((r) => (
          <div key={r.email} className="flex items-center gap-3">
            <div className="w-[120px] shrink-0">
              <p className="text-[12px] font-medium text-slate-700 truncate">{r.name}</p>
            </div>
            <div className="flex-1 h-6 bg-slate-100 rounded-full overflow-hidden flex">
              <div className="h-full bg-emerald-400 transition-all" style={{ width: `${(r.completed / maxTasks) * 100}%` }}
                title={`Completed: ${r.completed}`} />
              <div className="h-full bg-blue-400 transition-all" style={{ width: `${(r.in_progress / maxTasks) * 100}%` }}
                title={`In Progress: ${r.in_progress}`} />
              <div className="h-full bg-slate-300 transition-all" style={{ width: `${(r.pending / maxTasks) * 100}%` }}
                title={`Pending: ${r.pending}`} />
            </div>
            <span className="text-[11px] font-medium text-slate-600 w-10 text-right">{r.total_tasks}</span>
            <span className={`text-[11px] font-semibold w-10 text-right ${pctColor(r.completion_pct)}`}>{Math.round(r.completion_pct)}%</span>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-4 mt-3 pt-3 border-t border-slate-100">
        <div className="flex items-center gap-1.5 text-[10px] text-slate-500"><div className="w-3 h-3 rounded bg-emerald-400" /> Completed</div>
        <div className="flex items-center gap-1.5 text-[10px] text-slate-500"><div className="w-3 h-3 rounded bg-blue-400" /> In Progress</div>
        <div className="flex items-center gap-1.5 text-[10px] text-slate-500"><div className="w-3 h-3 rounded bg-slate-300" /> Pending</div>
      </div>
    </div>
  );
};


/* ══════════════════════════════════════════════
   MAIN PAGE
   ══════════════════════════════════════════════ */
export default function ProjectTracker() {
  const [view, setView] = useState("overview");
  const [projects, setProjects] = useState([]);
  const [kanbanData, setKanbanData] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [workload, setWorkload] = useState(null);
  const [filters, setFilters] = useState(null);
  const [kpis, setKpis] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);

  // Filter state
  const [search, setSearch] = useState("");
  const [filterProject, setFilterProject] = useState("");
  const [filterResource, setFilterResource] = useState("");
  const [filterTaskType, setFilterTaskType] = useState("");
  const [filterLanguage, setFilterLanguage] = useState("");

  // Fetch filters on mount
  useEffect(() => {
    axios.get(`${API}/project-tracker/filters`).then(res => setFilters(res.data)).catch(() => {});
  }, []);

  // Fetch data based on view + filters
  const fetchData = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (filterResource) params.resource = filterResource;
    if (filterTaskType) params.task_type = filterTaskType;
    if (filterLanguage) params.language = filterLanguage;

    try {
      // Always fetch projects for KPIs
      const projRes = await axios.get(`${API}/project-tracker/projects`, { params });
      setProjects(projRes.data.projects || []);
      setKpis(projRes.data.kpis || {});

      if (view === "overview") {
        const wRes = await axios.get(`${API}/project-tracker/workload`, { params });
        setWorkload(wRes.data);
      } else if (view === "kanban") {
        const kParams = { ...params };
        if (filterProject) kParams.project = filterProject;
        const res = await axios.get(`${API}/project-tracker/kanban`, { params: kParams });
        setKanbanData(res.data);
      } else if (view === "timeline") {
        const res = await axios.get(`${API}/project-tracker/timeline`, { params });
        setTimeline(res.data.timeline || []);
      }
    } catch {
      toast.error("Failed to load data");
    }
    setLoading(false);
  }, [view, search, filterProject, filterResource, filterTaskType, filterLanguage]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await axios.put(`${API}/project-tracker/tasks/${taskId}/status?status=${encodeURIComponent(newStatus)}`);
      toast.success(`Task moved to ${newStatus}`);
      fetchData();
    } catch {
      toast.error("Failed to update status");
    }
  };

  const viewModes = [
    { id: "overview", label: "Overview", icon: LayoutGrid },
    { id: "kanban", label: "Kanban", icon: Columns3 },
    { id: "timeline", label: "Timeline", icon: GanttChart },
    { id: "table", label: "Table", icon: Table2 },
  ];

  return (
    <div className="px-6 lg:px-8 py-6 max-w-full" data-testid="project-tracker-page">
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>
          Project Tracker
        </h1>
        <p className="text-sm text-slate-500 mt-1">Track progress across all projects, resources, and deadlines</p>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-6" data-testid="kpi-strip">
        <KpiCard label="Projects" value={projects.length || "0"} icon={FolderOpen} color="bg-violet-500" />
        <KpiCard label="Total Tasks" value={kpis.total_tasks?.toLocaleString() || "0"} icon={Table2} color="bg-blue-500" />
        <KpiCard label="Completed" value={kpis.completed_tasks?.toLocaleString() || "0"} sub={`${kpis.completion_pct || 0}%`} icon={CheckCircle2} color="bg-emerald-500" />
        <KpiCard label="Overdue" value={kpis.overdue_tasks || "0"} icon={AlertTriangle} color="bg-red-500" />
        <KpiCard label="Resources" value={kpis.active_resources || "0"} icon={Users} color="bg-amber-500" />
        <KpiCard label="Total Value" value={kpis.total_amount ? `₹${(kpis.total_amount / 1000).toFixed(0)}K` : "₹0"} icon={TrendingUp} color="bg-teal-500" />
      </div>

      {/* Toolbar: View Switcher + Filters */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-5 bg-white rounded-xl border border-slate-200/80 p-3" data-testid="toolbar">
        {/* View Tabs */}
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
          {viewModes.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setView(id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium transition-all ${
                view === id
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
              }`}
              data-testid={`view-${id}`}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap flex-1 justify-end">
          <div className="relative w-48">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <Input
              className="h-8 pl-8 text-[12px]"
              placeholder="Search projects..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              data-testid="filter-search"
            />
          </div>
          {view === "kanban" && filters?.projects && (
            <Select value={filterProject || "all_projects"} onValueChange={v => setFilterProject(v === "all_projects" ? "" : v)}>
              <SelectTrigger className="h-8 w-40 text-[12px]" data-testid="filter-project">
                <SelectValue placeholder="All Projects" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all_projects">All Projects</SelectItem>
                {filters.projects.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {filters?.resources && (
            <Select value={filterResource || "all_resources"} onValueChange={v => setFilterResource(v === "all_resources" ? "" : v)}>
              <SelectTrigger className="h-8 w-36 text-[12px]" data-testid="filter-resource">
                <SelectValue placeholder="All Resources" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all_resources">All Resources</SelectItem>
                {filters.resources.map(r => <SelectItem key={r.email} value={r.email}>{r.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {filters?.task_types && (
            <Select value={filterTaskType || "all_types"} onValueChange={v => setFilterTaskType(v === "all_types" ? "" : v)}>
              <SelectTrigger className="h-8 w-32 text-[12px]" data-testid="filter-task-type">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all_types">All Types</SelectItem>
                {filters.task_types.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {filters?.languages && (
            <Select value={filterLanguage || "all_langs"} onValueChange={v => setFilterLanguage(v === "all_langs" ? "" : v)}>
              <SelectTrigger className="h-8 w-32 text-[12px]" data-testid="filter-language">
                <SelectValue placeholder="All Languages" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all_langs">All Languages</SelectItem>
                {filters.languages.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {(search || filterResource || filterTaskType || filterLanguage || filterProject) && (
            <Button
              variant="ghost" size="sm" className="h-8 text-[11px] text-red-500 hover:text-red-600"
              onClick={() => { setSearch(""); setFilterResource(""); setFilterTaskType(""); setFilterLanguage(""); setFilterProject(""); }}
              data-testid="clear-filters"
            >
              <X className="w-3.5 h-3.5 mr-1" />Clear
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {view === "overview" && (
            <>
              <OverviewView projects={projects} onSelect={setSelectedProject} />
              <WorkloadBar workload={workload} />
            </>
          )}
          {view === "kanban" && (
            <KanbanView kanbanData={kanbanData} onStatusChange={handleStatusChange} />
          )}
          {view === "timeline" && (
            <TimelineView timeline={timeline} />
          )}
          {view === "table" && (
            <TableView projects={projects} onSelect={setSelectedProject} />
          )}
        </>
      )}

      {/* Detail Panel */}
      {selectedProject && (
        <ProjectDetailPanel project={selectedProject} onClose={() => setSelectedProject(null)} />
      )}
    </div>
  );
}
